import type { ChangeEvent } from 'react';
import { useCallback } from 'react';

import ShareOutlined from '@mui/icons-material/ShareOutlined';

import Button from '@/components/Buttons/Button';
import ShareDialog from '@/components/ShareDialog';
import getFactsheetLink from '@/features/Factsheet/utils/getFactsheetLink';

import useShareButton from './useShareButton';

import type { SxProps } from '@mui/material';
import { GoogleReCaptcha } from 'react-google-recaptcha-v3';
import useReCaptchaToken from '@/pages/Onboarding/hooks/useReCaptchaToken';

const ShareButton = ({
  title,
  additionalTitle,
  code,
  sx,
  iconSize = 24
}: {
  title?: string;
  additionalTitle?: string;
  code?: string;
  sx?: SxProps;
  iconSize?: number;
}) => {
  const { captchaToken, onVerify, refreshReCaptcha } = useReCaptchaToken();
  const {
    modalOpen,
    setModalOpen,
    content,
    setContent,
    recipient,
    handleChangeRecipient,
    handleCopyToClipboard,
    sendEmail,
    emailError
  } = useShareButton({ code, captchaToken });

  const shareUrl = getFactsheetLink(code);
  const headerSuffix = additionalTitle ? ` / ${additionalTitle}` : '';
  const header = `Share ${title}${headerSuffix}`;

  const handleClose = useCallback(() => {
    setModalOpen(false);
  }, [setModalOpen]);

  const handleContentChange = useCallback(
    (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setContent(e.target.value);
    },
    [setContent]
  );

  return (
    <>
      <div hidden>
        <GoogleReCaptcha
          action='share'
          refreshReCaptcha={refreshReCaptcha}
          onVerify={onVerify}
        />
      </div>
      <Button
        color='secondary'
        sx={{ width: 192, height: 48, fontSize: '16px', ...sx }}
        variant='contained'
        onClick={() => {
          setModalOpen(true);
        }}
      >
        <ShareOutlined
          sx={{
            width: iconSize,
            height: iconSize
          }}
        />
        Share
      </Button>
      <ShareDialog
        content={content}
        emailError={emailError}
        handleChangeRecipient={handleChangeRecipient}
        header={header}
        open={modalOpen}
        recipient={recipient}
        shareUrl={shareUrl}
        onClose={handleClose}
        onContentChange={handleContentChange}
        onCopyClick={handleCopyToClipboard}
        onSendEmail={sendEmail}
      />
    </>
  );
};

export default ShareButton;
